import { findOnboardingTarget } from './findTarget';

/**
 * Shared `onEnter` / `advanceWhen` pairs for steps that wait on a control instead of
 * offering a Next button. Spread one into a step:
 *
 *   ...watchChange(() => useFiltersStore.getState().overallTypeFilter)
 *
 * `onEnter` returns a cleanup where one is needed, which is what `OnboardingRoot`
 * expects, so the pair also survives a replay of the same step.
 */
export interface StepAdvance {
  onEnter: () => void | (() => void);
  advanceWhen: () => boolean;
}

/**
 * Advance once `read()` differs from what it returned when the step opened.
 *
 * The baseline is boxed rather than compared against a sentinel, so `undefined` and
 * `null` are ordinary values: before the first `onEnter` there is no baseline at all
 * and the step never advances on its own.
 */
export function watchChange<T>(read: () => T, equals: (a: T, b: T) => boolean = Object.is): StepAdvance {
  let baseline: [T] | null = null;
  return {
    onEnter: () => {
      baseline = [read()];
    },
    advanceWhen: () => baseline !== null && !equals(baseline[0], read()),
  };
}

export interface InputSettledOptions {
  /** Characters the field must hold before the step may advance. Default 1. */
  minLength?: number;
  /** Quiet time after the last keystroke. Default 700ms, comfortably over the 200ms tick. */
  idleMs?: number;
  /**
   * Require a value different from the one the field held on entry. Default true.
   *
   * Pass false when the step asks for exactly the value something else prefills — a
   * cost-price picker defaults a gram quantity to 100, so a step whose copy asks for
   * 100 g would wait forever for a change that never comes, with no Next to escape.
   * Gate such a step on the surrounding action instead, e.g.
   * `allOf(watchChange(productPicked), inputSettled(anchor, { changedFromInitial: false }))`.
   */
  changedFromInitial?: boolean;
}

/**
 * Advance once the user has typed something into the field behind `anchor` and stopped.
 *
 * The idle window is what keeps the 200ms advance tick from firing on the first
 * keystroke of a longer word. Blur and Enter are treated as "done typing" and skip the
 * wait, so a step never sits there after the user has visibly finished.
 */
export function inputSettled(anchor: string, options: InputSettledOptions = {}): StepAdvance {
  const { minLength = 1, idleMs = 700, changedFromInitial = true } = options;
  const FIELDS = 'input, textarea, select';
  type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

  let entered = false;
  let initial: string | null = null;
  let lastValue = '';
  let lastChangeAt = 0;
  let committed = false;
  let bound: Field | null = null;
  let detach: (() => void) | null = null;
  let observer: MutationObserver | null = null;
  let userEdited = false;

  const field = (): Field | null => {
    const target = findOnboardingTarget(anchor);
    if (!target) return null;
    return target.matches(FIELDS) ? (target as Field) : target.querySelector<Field>(FIELDS);
  };
  const valueOf = () => field()?.value ?? '';
  // A known user edit stands in for the changed-from-initial rule. If adoption ever did
  // swallow the typing as its baseline, the step still advances instead of hanging.
  const passes = (value: string) =>
    value.length >= minLength && (!changedFromInitial || userEdited || value !== initial);

  /** Anything the user types inside the anchor counts, whoever owns the field. */
  const onUserInput = (event: Event) => {
    const node = event.target;
    if (node instanceof Element && node.closest(`[data-onboarding~="${anchor}"]`)) userEdited = true;
  };

  const release = () => {
    detach?.();
    detach = null;
    bound = null;
    observer?.disconnect();
    observer = null;
    document.removeEventListener('input', onUserInput, true);
  };

  /** Bind late-mounting fields: a target can arrive well after the step opens. */
  const bind = () => {
    const el = field();
    if (el === bound) return;
    release();
    if (!el) return;
    bound = el;
    // The field may have mounted after `onEnter`, so this is the first honest baseline.
    if (initial === null) {
      initial = el.value;
      lastValue = el.value;
      lastChangeAt = Date.now();
    }
    const commit = () => {
      if (passes(valueOf())) committed = true;
    };
    // The element is a union of three field types, so `addEventListener` here is the
    // untyped overload and the event arrives as a plain `Event`.
    const onKeyDown = (event: Event) => {
      if ((event as KeyboardEvent).key === 'Enter') commit();
    };
    el.addEventListener('blur', commit);
    el.addEventListener('keydown', onKeyDown);
    detach = () => {
      el.removeEventListener('blur', commit);
      el.removeEventListener('keydown', onKeyDown);
    };
  };

  return {
    onEnter: () => {
      release();
      entered = true;
      committed = false;
      initial = null;
      lastValue = '';
      lastChangeAt = Date.now();
      userEdited = false;
      document.addEventListener('input', onUserInput, true);
      bind();
      // Adopt the baseline the moment a late field mounts. Waiting for the next 200ms
      // tick is long enough for an autofocused field to be typed into, and that typing
      // would then become the baseline the step is waiting to see change.
      if (initial === null) {
        observer = new MutationObserver(() => {
          bind();
          if (initial !== null) {
            observer?.disconnect();
            observer = null;
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
      return () => {
        release();
        entered = false;
      };
    },
    advanceWhen: () => {
      if (!entered) return false;
      bind();
      if (initial === null) return false;
      // A commit already satisfied the content rules. Check it before reading the field
      // again: an inline editor is often torn down by the very blur that committed it,
      // and an empty box that is no longer on screen must not undo the user's edit.
      if (committed) return true;
      const value = valueOf();
      if (value !== lastValue) {
        lastValue = value;
        lastChangeAt = Date.now();
      }
      if (!passes(value)) return false;
      return Date.now() - lastChangeAt >= idleMs;
    },
  };
}

/**
 * Advance once a DOM reading of the target changes — `aria-sort` on a column header,
 * `aria-expanded` on a disclosure. For controls whose state never reaches a store.
 */
export function controlChanged(anchor: string, read: (el: HTMLElement) => string): StepAdvance {
  let baseline: string | null = null;
  const current = (): string | null => {
    const el = findOnboardingTarget(anchor);
    return el ? read(el) : null;
  };
  return {
    onEnter: () => {
      baseline = current();
    },
    advanceWhen: () => {
      const value = current();
      if (value === null) return false;
      // A target that mounted late was never part of the baseline; adopt it instead of
      // reporting the arrival itself as the user's change.
      if (baseline === null) {
        baseline = value;
        return false;
      }
      return value !== baseline;
    },
  };
}

/**
 * Advance once every part is satisfied — "a product is picked AND its quantity has
 * settled". Each part is polled on every tick rather than short-circuiting, so a part
 * that tracks idle time keeps its clock running while an earlier one is still false.
 */
export function allOf(...advances: StepAdvance[]): StepAdvance {
  return {
    onEnter: () => {
      const cleanups: (() => void)[] = [];
      for (const advance of advances) {
        const cleanup = advance.onEnter();
        if (cleanup) cleanups.push(cleanup);
      }
      return () => cleanups.forEach(cleanup => cleanup());
    },
    advanceWhen: () => advances.map(advance => advance.advanceWhen()).every(Boolean),
  };
}

/**
 * Run `prepare` before a wait-for-change pair takes its baseline, so a step that both
 * sets state and then watches that same state cannot read its own setup as the user's
 * change. Both cleanups are returned, the watcher's first.
 */
export function afterEnter(prepare: () => void | (() => void), advance: StepAdvance): StepAdvance {
  return {
    onEnter: () => {
      const undoPrepare = prepare();
      const undoAdvance = advance.onEnter();
      return () => {
        undoAdvance?.();
        undoPrepare?.();
      };
    },
    advanceWhen: advance.advanceWhen,
  };
}
